import { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Bell, SlidersHorizontal, Search, Zap, ArrowLeft,
  UserPlus, Loader2, Heart, MessageCircle, Megaphone,
  TrendingUp as TrendingIcon, Users, Sparkles, X,
  BarChart3,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import logoImage from '../assets/f736bb54a1ccb5a9a30d8b8ec9d3974d4b7ef9d6.png';
import logoImageWhite from '../assets/fac7d53f9fcb6f491ee2fb6641c18821c53c606e.png';
import goldIcon from '../assets/gold icon.png';
// Dark mudcloth background — same asset used by ParallaxBackground in V1 dark mode
import darkPatternBg from '../assets/3aa95779ad637ef1d40ddc06f12ff02444cc906d.png';
import lightPatternBg from '../assets/8f320715a0789cd66ecbac8ed3c33f41f7bf02e3.png';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { useNotificationBadges } from './NotificationContext';
import { useTheme } from './ThemeProvider';
import { useDiscoverContext } from '../contexts/DiscoverContext';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { ScreenLoader } from './ScreenLoader';
import { DiscoverService } from '../services/DiscoverService';
import { CommunityDiscoveryService } from '../services/CommunityDiscoveryService';
import type { SimilarInterestUser, GroupUpdate } from '../services/DiscoverService';
import { YapService, EconomyService, SearchService, UserService } from '../services/api';
import { calculateAge, formatTimeAgo } from './utils/timeFormat';
import { SWIPE_COSTS } from '../constants/economy';
import { User, Yap } from '../types';
import { SectionHeader, Tagboard, MiniDiscoverCard } from './ui';
import { HScrollDeck } from './ui/HScrollDeck';
import { ActivityStatus } from './ui/ActivityStatus';
import { PressableCard } from './ui/PressableCard';
import type { TagItem } from './ui';
import { DiscoverScreen } from './DiscoverScreen';
import { DiscoverFilterModal } from './DiscoverFilterModal';
import type { FilterOptions } from './DiscoverFilterModal';
import { InsufficientFundsModal } from './InsufficientFundsModal';
import { UserDetailModal } from './UserDetailModal';
import { FlagImage } from './utils/countryFlags';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Input } from './ui/input';
import { formatCount } from '../utils/numberFormat';
import { SCREENS } from '../constants/navigation';
import { toast } from 'sonner';
import RankBadge from './RankBadge';
import { VerifiedBadge } from './VerifiedBadge';
import { FriendBadge } from './ui/FriendBadge';
import { SimilarBadge } from './ui/SimilarBadge';
import { isSimilarToMe } from './utils/similarity';
import { getUserAvatar } from '../utils/userUtils';

interface DiscoverScreenV2Props {
  onMatch?: (d: any) => void;
}

// ── Tag helper ───────────────────────────────────────────────────────────────
// Builds TagItem[] for Tagboard from a SimilarInterestUser, matching tags first.
function buildTagItems(user: SimilarInterestUser): TagItem[] {
  const matchingSet = new Set(user.overlappingTags ?? []);
  const all = [...new Set([...(user.identities ?? []), ...(user.interests ?? [])])];
  const sorted = [
    ...all.filter(t => matchingSet.has(t)),
    ...all.filter(t => !matchingSet.has(t)),
  ];
  return sorted.slice(0, 8).map((tag, i) => ({
    id: `${user.id}-${tag}-${i}`,
    label: tag,
    isMatching: matchingSet.has(tag),
  }));
}

// ── Skeleton card ─────────────────────────────────────────────────────────────
function SkeletonCard({ className = '' }: { className?: string }) {
  return <div className={`rounded-2xl bg-white/8 animate-pulse ${className}`} />;
}

// ── Trending Yap card — mirrors YapTabScreen's card visual exactly ─────────────
// Interaction is tap-to-detail (spec allows this for horizontal scroll context).
// Card width is ~50% viewport so two are visible simultaneously per spec.
function TrendingYapCard({ yap, onTap, isFriendAuthor }: { yap: Yap; onTap: () => void; isFriendAuthor?: boolean }) {
  return (
    <motion.div
      className="shrink-0 rounded-2xl bg-[#1a1a1a] border border-white/10 card-shadow cursor-pointer overflow-hidden"
      style={{ width: 'calc(50% - 6px)', minWidth: 260, height: 200 }}
      whileTap={{ scale: 0.98 }}
      onClick={onTap}
    >
      {/* Tighter gaps when a poll marker is present. The tile is a hard 200px
          with overflow-hidden, and at gap-3 the extra row put the content a few
          pixels over — which justify-center then clips off both ends, shaving
          the avatar. gap-2 buys back the headroom. */}
      <div className={`p-5 h-full flex flex-col justify-center ${(yap as any).poll?.options?.length ? 'gap-2' : 'gap-3'}`}>
        {/* Header: avatar + name + rank + flag + username */}
        <div className="flex items-center gap-3 min-w-0">
          <Avatar className="w-10 h-10 shadow-sm shrink-0">
            <AvatarImage src={yap.avatar} />
            <AvatarFallback>{(yap.author || '?')[0]}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-bold text-white truncate text-sm leading-tight">{yap.author}</span>
              {yap.rank && ['watcher', 'legion', 'verified', 'union'].includes(yap.rank) && (
                <RankBadge rank={yap.rank} size={16} />
              )}
              <span className="text-[11px] text-white/30 shrink-0">&bull;</span>
              <FlagImage
                countryName={yap.authorCountryFlag || yap.country}
                className="w-4 aspect-3/2 object-contain rounded shrink-0"
              />
            </div>
            {/* The friend pill rides along on the handle line rather than
                taking a row of its own. At 15px tall it is shorter than the
                line it now shares, so it costs the tile no height at all —
                whereas its own row plus my-1 came to roughly 23px, which is
                what pushed friends' tiles past 200px and had justify-center
                clip the avatar and name off the top.

                forceDark because this tile is dark in both themes: left to
                follow the app theme the pill renders dark green on #1a1a1a in
                light mode, which is the same way the poll card was disappearing
                here. */}
            <div className="flex items-center gap-1 text-xs text-white/40 mt-0.5 min-w-0">
              <span className="truncate">@{(yap.username || '').replace(/^@/, '')}</span>
              <span className="shrink-0 opacity-60">· {yap.time}</span>
              {isFriendAuthor && <FriendBadge className="shrink-0" forceDark />}
            </div>
          </div>
        </div>

        {/* Content — line-clamp prevents runaway height on long yaps. One line
            shorter when a poll marker sits below it. */}
        <p className={`text-white/85 text-sm leading-relaxed ${(yap as any).poll?.options?.length ? 'line-clamp-2' : 'line-clamp-3'}`}>
          {yap.content}
        </p>

        {/* A marker, not the poll itself.

            An actual PollCard did not fit: this tile is a hard 200px with
            overflow-hidden and justify-center, and header + text + poll + stats
            came to roughly 286px, so the overflow was clipped off the top and
            bottom — taking half the avatar and name with it. A single line says
            the yap has a poll and leaves the tile intact; voting happens on the
            yap itself, which is where a tap already goes.

            Colours are inline because this tile is dark in both themes, while
            PollCard follows the app theme — which is the other half of why the
            embedded poll looked broken here: in light mode it painted near-black
            text and bars onto #1a1a1a. */}
        {(yap as any).poll?.options?.length > 0 && (
          <div
            className="flex items-center gap-1.5 self-start rounded-full px-2 py-1"
            style={{ backgroundColor: 'rgba(68,120,234,0.16)' }}
          >
            <BarChart3 className="w-3.5 h-3.5 shrink-0" style={{ color: '#7aa2f0' }} />
            <span className="text-xs font-semibold" style={{ color: '#7aa2f0' }}>
              Poll · tap to vote
            </span>
          </div>
        )}

        {/* Stats row — display only; full interactions on detail screen */}
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5 text-xs text-white/40">
            <Heart className="w-3.5 h-3.5" />
            {formatCount(yap.likes)}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-white/40">
            <MessageCircle className="w-3.5 h-3.5" />
            {formatCount(yap.comments)}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-white/40">
            <Megaphone className="w-3.5 h-3.5" />
            {formatCount(yap.shouts ?? 0)}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

// ── Group Update card ────────────────────────────────────────────────────────
function GroupUpdateCard({ group, onTap }: { group: GroupUpdate; onTap: () => void }) {
  const postTime = group.latestPost
    ? (() => { try { return formatTimeAgo(new Date(group.latestPost.timestamp)); } catch { return ''; } })()
    : '';

  return (
    <motion.div
      className="shrink-0 rounded-2xl overflow-hidden cursor-pointer border border-white/10"
      style={{ width: 'calc(80% - 8px)', minWidth: 280, background: '#1a1a1a' }}
      whileTap={{ scale: 0.98 }}
      onClick={onTap}
    >
      {/* Banner */}
      <div
        className="relative w-full flex items-end"
        style={{
          height: 68,
          background: group.bannerUrl
            ? `url(${group.bannerUrl}) center/cover no-repeat`
            : 'linear-gradient(135deg, #1e1f4a 0%, #2d1e6e 100%)',
        }}
      >
        {/* Bottom fade */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
        {/* Corner vignette */}
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.45) 100%)' }} />
        <div className="relative px-3 pb-2 flex items-center gap-2 w-full min-w-0">
          <p
            className="font-bold text-white text-sm truncate flex-1"
            style={{ textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}
          >
            {group.name}
          </p>
          <span
            className="shrink-0 font-bold uppercase tracking-wide rounded-full"
            style={{
              fontSize: '7px',
              padding: '2px 7px',
              background: group.type === 'tribe' ? 'rgba(68,120,234,0.85)' : 'rgba(120,68,234,0.85)',
              color: '#fff',
            }}
          >
            {group.type}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="px-3 pt-3 pb-3 flex flex-col gap-2">
        {/* Tags */}
        {group.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {group.tags.slice(0, 4).map(tag => (
              <span
                key={tag}
                className="text-white font-bold rounded-full whitespace-nowrap"
                style={{ fontSize: '9px', padding: '3px 8px', background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)' }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Divider */}
        <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', marginTop: 2, marginBottom: 2 }} />

        {/* Latest post — yap format, read-only preview (no interactions for non-members) */}
        {group.latestPost ? (
          <div className="flex flex-col gap-2">
            {/* Section label */}
            <p className="font-semibold" style={{ fontSize: '10px', color: 'rgba(255,255,255,0.38)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Recent Post
            </p>
            {/* Author row */}
            <div className="flex items-center gap-2 min-w-0">
              <Avatar className="w-7 h-7 shrink-0 rounded-full overflow-hidden">
                <AvatarImage src={group.latestPost.avatar} className="rounded-full" />
                <AvatarFallback className="rounded-full" style={{ fontSize: 10 }}>{(group.latestPost.author || '?')[0]}</AvatarFallback>
              </Avatar>
              <div className="flex flex-col min-w-0 leading-tight">
                <span className="font-bold text-white truncate" style={{ fontSize: '11px' }}>
                  {group.latestPost.author}
                </span>
                <span className="truncate" style={{ fontSize: '10px', color: 'rgba(255,255,255,0.38)' }}>
                  {group.latestPost.username ? `@${group.latestPost.username}` : ''}{postTime ? ` · ${postTime}` : ''}
                </span>
              </div>
            </div>
            {/* Content */}
            <p className="text-white/80 leading-relaxed line-clamp-2" style={{ fontSize: '12px' }}>
              {group.latestPost.content}
            </p>
          </div>
        ) : (
          <p className="text-white/50 leading-relaxed line-clamp-2" style={{ fontSize: '12px' }}>
            {group.description}
          </p>
        )}
      </div>
    </motion.div>
  );
}

/**
 * DiscoverScreenV2 — The new Discover experience.
 *
 * Feature-flagged: only shown when userProfile.betaDiscover === true.
 * Gate is in AppRouter.tsx DiscoverScreenSelector.
 *
 * Three sections, all dark-background:
 *   1. New Yappers This Month — portrait rotator cards
 *   2. Similar Interests to Me — PortfolioRotator + Tagboard pairs + Connect button
 *   3. Trending Yaps — compact half-width yap cards → tap to detail
 *
 * SWIPE button: renders V1 (DiscoverScreen) as a full-screen overlay with an
 * ArrowLeft dismiss button. V2's scroll position is preserved on return.
 */
export function DiscoverScreenV2({ onMatch }: DiscoverScreenV2Props) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { unreadCount } = useNotificationBadges();
  const {
    userProfile,
    updateProfile,
    sendFriendRequest,
    isFriend,
    hasPendingRequestTo,
    hasPendingRequestFrom,
    sentRequestIds = [],
  } = useUserData();

  // Discover must exclude both directions: people I blocked and people who
  // blocked me (mirrored into blockedBy server-side). This used to be derived
  // inline here, which is how it drifted: the set was wired into the
  // people-discovery queries only, leaving Trending Yaps and group post previews
  // unfiltered. Sourcing it from the shared hook keeps this screen in step with
  // every other content surface.
  const {
    blockedSet, isHiddenContent, filterVisible,
    filterVisibleGroups, isMutedGroup, filtersNothing,
  } = useContentFilter();
  const blockedEitherWay = useMemo(() => [...blockedSet], [blockedSet]);

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [newYappers, setNewYappers]       = useState<User[]>([]);
  const [similarUsers, setSimilarUsers]   = useState<SimilarInterestUser[]>([]);
  const [trendingYaps, setTrendingYaps]   = useState<Yap[]>([]);
  const [groupUpdates, setGroupUpdates]   = useState<GroupUpdate[]>([]);
  // Groups matching the selected Identities filter (tribes + forums), fetched by
  // tag so the Group Updates section responds to the filter. null = no tag filter
  // active (show the normal recent group updates).
  const [taggedGroups, setTaggedGroups]   = useState<GroupUpdate[] | null>(null);
  const [loadingTaggedGroups, setLoadingTaggedGroups] = useState(false);
  const [loadingYappers, setLoadingYappers]     = useState(true);
  const [loadingSimilar, setLoadingSimilar]     = useState(true);
  const [loadingTrending, setLoadingTrending]   = useState(true);
  const [loadingGroups, setLoadingGroups]       = useState(true);
  const [yappersRefreshKey, setYappersRefreshKey] = useState(0);
  const [isRefreshingYappers, setIsRefreshingYappers] = useState(false);

  // The screen is ready only once every section has resolved for the first time.
  // Declared here rather than beside the render because the hook has to sit
  // above the showInDiscover early return below.
  const isInitialLoading = loadingYappers || loadingSimilar || loadingTrending || loadingGroups;
  useScreenReady(!isInitialLoading);

  // Real progress, not a timer: each of the four sections fetches independently,
  // so the share of them that has resolved IS how far along the screen is. This
  // is the slowest screen in the app (5 parallel service calls), which is why it
  // is the one screen that reports a number instead of just pulsing.
  const loadProgress =
    ([loadingYappers, loadingSimilar, loadingTrending, loadingGroups]
      .filter(l => !l).length) / 4;

  // Pull-to-refresh has been removed — feedback was that the gesture fired
  // accidentally during normal scrolling. The Newest Yappers section keeps its
  // own dedicated refresh button (yappersRefreshKey); the other sections
  // re-fetch when the user changes (userProfile.id dep on the fetch effect).
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Opening a group or an account unmounts this screen and remounts it on the
  // way back, so the reader landed at the top of a list they had scrolled a
  // long way down. The ref was already attached to the container here and never
  // read; this is what reads it.
  const { onScroll: onDiscoverScroll } = useScrollRestoration('discover_v2', scrollContainerRef);

  // ── UI ───────────────────────────────────────────────────────────────────────
  const { swipeModeActive, setSwipeModeActive } = useDiscoverContext();
  const [previewUser, setPreviewUser]             = useState<User | null>(null);
  const [showInsufficientFunds, setShowInsufficientFunds] = useState(false);
  const [connectingUser, setConnectingUser]       = useState<string | null>(null);
  const [connectedUserIds, setConnectedUserIds]   = useState<Set<string>>(new Set());

  // ── Filters (applied to Newest Yappers only) ─────────────────────────────────
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterOptions>({
    location: 'international',
    ageRange: [18, 100],
    interests: [],
    tribes: [],
  });

  // Correct ageRange default once profile loads — minors must see [13,17], not [18,100].
  const ageFilterInitialized = useRef(false);
  useEffect(() => {
    if (ageFilterInitialized.current || !userProfile) return;
    ageFilterInitialized.current = true;
    const age = calculateAge(userProfile.dateOfBirth) ?? (userProfile as any).age ?? 0;
    if (age < 18) {
      setActiveFilters(prev => ({ ...prev, ageRange: [13, 17] }));
    }
  }, [userProfile]);

  // Viewer's correct default age range — used for filter init and "Clear filters" reset.
  const viewerDefaultAgeRange: [number, number] = (() => {
    const age = calculateAge(userProfile?.dateOfBirth) ?? (userProfile as any)?.age ?? 0;
    return age > 0 && age < 18 ? [13, 17] : [18, 100];
  })();

  // When Identities are selected in the filter, fetch tribes + forums that match
  // those tags (by group tags + member tags + name, ranked in getCommunities) so
  // the Group Updates section reflects the tag filter — general groups included.
  useEffect(() => {
    const interests = activeFilters.interests;
    if (!interests || interests.length === 0) { setTaggedGroups(null); return; }
    let cancelled = false;
    setLoadingTaggedGroups(true);
    (async () => {
      try {
        const [tribesRes, forumsRes] = await Promise.all([
          CommunityDiscoveryService.getCommunities('tribes', 'for_you', interests, 10, isMutedGroup),
          CommunityDiscoveryService.getCommunities('forums', 'for_you', interests, 10, isMutedGroup),
        ]);
        if (cancelled) return;
        const toGU = (g: any, type: 'tribe' | 'forum'): GroupUpdate => ({
          id: g.id,
          type,
          name: g.name ?? '',
          bannerUrl: g.bannerUrl ?? g.bannerImage ?? null,
          tags: g.tags ?? g.identities ?? [],
          memberTags: g.memberTags ?? [],
          description: g.description ?? '',
          memberCount: g.memberCount ?? 0,
          latestPost: null,
          lastActivityAt: g.lastActivityAt?.toMillis?.() ?? 0,
        });
        setTaggedGroups([
          ...(tribesRes as any[]).map(g => toGU(g, 'tribe')),
          ...(forumsRes as any[]).map(g => toGU(g, 'forum')),
        ]);
      } catch (e) {
        console.error('[V2] Tagged groups fetch failed:', e);
        if (!cancelled) setTaggedGroups([]);
      } finally {
        if (!cancelled) setLoadingTaggedGroups(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeFilters.interests]);

  // Group Updates shown: the tag-matched set when Identities are selected,
  // otherwise the normal recent-activity feed.
  //
  // 🚫 A group's preview shows its latest post, so a blocked author reached
  // Discover through that preview even though the group itself is fine. Drop the
  // preview rather than the group — the group stays discoverable, just without
  // quoting someone the viewer has blocked.
  // A muted word in the group's own name/description hides the group entirely
  // from discovery; a muted or blocked *preview* only drops the preview, since
  // the group itself is fine.
  const displayedGroups = useMemo(() => {
    const base = taggedGroups !== null ? taggedGroups : groupUpdates;
    if (filtersNothing) return base;
    return filterVisibleGroups(base).map(g =>
      g.latestPost && isHiddenContent(g.latestPost) ? { ...g, latestPost: null } : g,
    );
  }, [taggedGroups, groupUpdates, filtersNothing, filterVisibleGroups, isHiddenContent]);

  // 🚫 Trending Yaps are curated, not person-scoped, so a blocked author's yap
  // could still surface here — the blocked set was only ever applied to the
  // people-discovery queries below.
  const visibleTrendingYaps = useMemo(
    () => filterVisible(trendingYaps),
    [trendingYaps, filterVisible],
  );

  // ── Search ───────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery]           = useState('');
  const [searchResults, setSearchResults]       = useState<User[]>([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  // Blocking hides both ways, and search was the one surface that never applied
  // it: a blocked user still appeared by name the moment you typed it. Filtered
  // at render rather than where the results are set, so a block made during the
  // session takes effect immediately, and so the mod-augmented cross-bracket
  // results merged in later are covered by the same rule.
  const visibleSearchResults = useMemo(
    () => searchResults.filter(u => !blockedSet.has(String(u.id))),
    [searchResults, blockedSet],
  );
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  // Tracks the latest query so async results from a superseded keystroke don't
  // overwrite the current ones.
  const latestQueryRef = useRef('');
  // Watcher/Legion search across all age brackets (server-verified). Regular
  // users get the bracketed client search, so adults never see minors.
  const viewerIsMod = ['legion', 'watcher'].includes(String((userProfile as any)?.rank || '').toLowerCase());

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true; // reset after Strict Mode's simulated unmount/remount
    return () => { isMounted.current = false; };
  }, []);


  // ── Data fetch ───────────────────────────────────────────────────────────────
  // Dependencies: userId (user loads/changes) + refreshKey (pull-to-refresh).
  // New Yappers is always fetched fresh (cheap Firestore read, Fisher-Yates shuffle).
  // Similar Interests pool is cached 30 min in DiscoverService; only the random
  // SELECTION of 10 from the pool varies on each fetch call.
  useEffect(() => {
    if (!userProfile?.id) return;
    let cancelled = false;
    const uid = String(userProfile.id);

    const fetchAll = async () => {
      // Build exclusion list (self + friends + blocked + already-requested)
      let excludeIds: string[] = [uid, ...blockedEitherWay, ...sentRequestIds];
      try {
        const friends = await UserService.getFriends(uid);
        const friendIds = friends.map((f: any) => String(f.id ?? f));
        excludeIds = [...new Set([uid, ...friendIds, ...blockedEitherWay, ...sentRequestIds])];
      } catch { /* proceed with minimal exclusion on error */ }

      if (cancelled) return;

      // Determine viewer's age for age-segregation filtering.
      // dateOfBirth is authoritative; stored age field is the fallback.
      const viewerDob = userProfile?.dateOfBirth;
      // Fail-closed: if no DOB, treat viewer as minor (0 < 18) so they can't reach adult candidates.
      // Regex parse avoids new Date('YYYY-MM-DD') UTC midnight off-by-one in UTC- zones.
      // Fail-closed: invalid/absent DOB → 0, treated as minor so viewer can't reach adult candidates.
      const viewerAge = viewerDob
        ? (() => {
            const mm = viewerDob.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
            if (!mm) return 0;
            const b = new Date(parseInt(mm[1]), parseInt(mm[2]) - 1, parseInt(mm[3]));
            if (isNaN(b.getTime())) return 0;
            const t = new Date();
            let a = t.getFullYear() - b.getFullYear();
            if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--;
            return a;
          })()
        : 0;

      // Trending Yaps + Group Updates: independent — fire immediately
      YapService.fetchTrendingYaps()
        .then(r => { if (!cancelled) setTrendingYaps(r); })
        .catch(e => console.error('[V2] Trending Yaps:', e))
        .finally(() => { if (!cancelled) setLoadingTrending(false); });

      DiscoverService.fetchGroupUpdates(8, isMutedGroup)
        .then(r => { if (!cancelled) setGroupUpdates(r); })
        .catch(e => console.error('[V2] Group Updates:', e))
        .finally(() => { if (!cancelled) setLoadingGroups(false); });

      // Similar Interests and New Yappers run CONCURRENTLY.
      //
      // New Yappers used to sit behind `await fetchSimilarInterests`, so the two
      // heaviest user queries on the screen ran back to back and the last
      // section on the page could not start loading until the one above it had
      // completely finished. That serialisation was the bulk of the wait.
      //
      // The dependency it encoded is real but it is only a DEDUPE: New Yappers
      // excludes the full Similar Interests pool so the same person cannot appear
      // in both rows. That does not have to be known before the query is issued,
      // only before the result is rendered — so the fetch starts immediately with
      // the exclusions already known, and the pool is subtracted from whatever
      // comes back once Similar resolves. Same guarantee, no waiting.
      const similarPromise = DiscoverService
        .fetchSimilarInterests(uid, excludeIds, { viewerAge })
        .then(r => {
          if (!cancelled) setSimilarUsers(r);
          return r;
        })
        .catch(e => {
          console.error('[V2] Similar Interests:', e);
          return [] as SimilarInterestUser[];
        })
        .finally(() => { if (!cancelled) setLoadingSimilar(false); });

      const newYappersPromise = DiscoverService
        .fetchNewYappersThisMonth(uid, excludeIds, { limit: 50, viewerAge })
        .catch(e => {
          console.error('[V2] New Yappers:', e);
          return [] as any[];
        });

      void (async () => {
        const [, yappers] = await Promise.all([similarPromise, newYappersPromise]);
        if (cancelled) return;
        // Resolved only now, because the pool is populated by the call above.
        const similarPoolIds = DiscoverService.getSimilarInterestPoolIds(uid);
        setNewYappers((yappers as any[]).filter(y => !similarPoolIds.has(String(y.id))));
        setLoadingYappers(false);
      })();
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [userProfile?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // ^ blockedUsers omitted — mid-session blocks handled at render time.

  // Standalone Newest Yappers refresh — triggered by the section's refresh button.
  // Skips on first mount (yappersRefreshKey === 0) since the main effect handles that.
  useEffect(() => {
    if (yappersRefreshKey === 0 || !userProfile?.id) return;
    let cancelled = false;
    const uid = String(userProfile.id);
    const dob = userProfile.dateOfBirth;
    const mm = dob?.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    let viewerAge = 0;
    if (mm) {
      const b = new Date(parseInt(mm[1]), parseInt(mm[2]) - 1, parseInt(mm[3]));
      if (!isNaN(b.getTime())) {
        const t = new Date();
        viewerAge = t.getFullYear() - b.getFullYear();
        if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) viewerAge--;
      }
    }
    const similarPoolIds = DiscoverService.getSimilarInterestPoolIds(uid);
    const exclude = [...new Set([uid, ...Array.from(similarPoolIds)])];
    setIsRefreshingYappers(true);
    DiscoverService.fetchNewYappersThisMonth(uid, exclude, { limit: 30, viewerAge })
      .then(r => { if (!cancelled) setNewYappers(r); })
      .catch(e => console.error('[V2] Yappers refresh:', e))
      .finally(() => { if (!cancelled) setIsRefreshingYappers(false); });
    return () => { cancelled = true; };
  }, [yappersRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Search ───────────────────────────────────────────────────────────────────
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setSearchQuery(q);
    latestQueryRef.current = q;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (q.trim().length >= 1) {
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          // Determine viewer's age bracket for the search query.
          // Firestore rules require the query's ageGroup value to match the
          // viewer's actual ageGroup in Firestore — a mismatch silently returns [].
          const sAgeGroup: 'adult' | 'minor' = (() => {
            const stored = (userProfile as any)?.ageGroup;
            if (stored === 'adult' || stored === 'minor') return stored;
            const age = calculateAge(userProfile?.dateOfBirth);
            if (age !== null) return age >= 18 ? 'adult' : 'minor';
            const storedAge = (userProfile as any)?.age;
            if (typeof storedAge === 'number') return storedAge >= 18 ? 'adult' : 'minor';
            return 'minor';
          })();

          // Fast path: instant bracketed client search (same speed as before).
          const base = await SearchService.searchUsers(q.toLowerCase(), String(userProfile?.id), sAgeGroup, viewerIsMod);
          if (latestQueryRef.current !== q) return; // superseded by a newer keystroke
          setSearchResults(base);
          setShowSearchDropdown(true);

          // Mods (Watcher/Legion): augment with cross-bracket matches (e.g. minors)
          // from the rank-verified Cloud Function, merged in as they arrive so the
          // dropdown stays instant instead of waiting on the server round-trip.
          if (viewerIsMod) {
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const fn = httpsCallable(getFunctions(), 'modSearchUsers');
            const res: any = await fn({ searchTerm: q });
            if (latestQueryRef.current !== q) return; // superseded
            const extra = (res?.data?.users || []) as User[];
            setSearchResults(prev => {
              const seen = new Set(prev.map(u => String(u.id)));
              const merged = [...prev];
              for (const u of extra) if (!seen.has(String(u.id))) merged.push(u);
              return merged;
            });
          }
        } catch (err) {
          console.error('[DiscoverV2] Search error:', err);
        }
      }, 500);
    } else {
      setSearchResults([]);
      setShowSearchDropdown(false);
    }
  };

  // ── Connect (10-gold classic swipe — New Yappers) ────────────────────────────
  const connectNewYapper = async (targetUser: User) => {
    if (!userProfile) return;
    const uid = String(targetUser.id);
    if (connectedUserIds.has(uid) || isFriend(uid) || hasPendingRequestTo(uid)) return;

    if (hasPendingRequestFrom(uid)) {
      setConnectingUser(uid);
      try {
        const result = await sendFriendRequest(
          uid, targetUser.name, targetUser.username,
          getUserAvatar(targetUser as any)
        );
        setConnectedUserIds(prev => new Set([...prev, uid]));
        if (result?.isMatch) onMatch?.(targetUser);
      } catch { toast.error('Failed to accept request'); }
      finally { if (isMounted.current) setConnectingUser(null); }
      return;
    }

    // Similar users always cost 30; non-similar 10 — regardless of section.
    const newYapperCost = isSimilarToMe(userProfile, targetUser) ? SWIPE_COSTS.matching : SWIPE_COSTS.classic;
    const balance = userProfile.goldBalance ?? userProfile.gold ?? 0;
    if (balance < newYapperCost) {
      setShowInsufficientFunds(true);
      return;
    }

    setConnectingUser(uid);
    try {
      const econ = await EconomyService.deductGold(
        String(userProfile.id), newYapperCost,
        newYapperCost === SWIPE_COSTS.matching ? 'discover_swipe_matching' : 'discover_swipe_classic'
      );
      if (econ.success && econ.newBalance !== undefined) {
        updateProfile({ gold: econ.newBalance, goldBalance: econ.newBalance });
      }
      const result = await sendFriendRequest(
        uid, targetUser.name, targetUser.username,
        getUserAvatar(targetUser as any)
      );
      setConnectedUserIds(prev => new Set([...prev, uid]));
      if (result?.isMatch) onMatch?.(targetUser);
    } catch { toast.error('Failed to send request'); }
    finally { if (isMounted.current) setConnectingUser(null); }
  };

  // ── Connect (30-gold matching swipe — Similar Interests) ─────────────────────
  const connectWithUser = async (targetUser: SimilarInterestUser) => {
    if (!userProfile) return;
    const uid = String(targetUser.id);
    if (connectedUserIds.has(uid) || isFriend(uid) || hasPendingRequestTo(uid)) return;

    // Mutual match fast-path — they already sent a request, accept for free
    if (hasPendingRequestFrom(uid)) {
      setConnectingUser(uid);
      try {
        const result = await sendFriendRequest(
          uid, targetUser.name, targetUser.username,
          getUserAvatar(targetUser as any)
        );
        setConnectedUserIds(prev => new Set([...prev, uid]));
        if (result?.isMatch) onMatch?.(targetUser);
      } catch { toast.error('Failed to accept request'); }
      finally { if (isMounted.current) setConnectingUser(null); }
      return;
    }

    // Standard path — costs 30 gold
    const balance = userProfile.goldBalance ?? userProfile.gold ?? 0;
    if (balance < SWIPE_COSTS.matching) {
      setShowInsufficientFunds(true);
      return;
    }

    setConnectingUser(uid);
    try {
      const econ = await EconomyService.deductGold(
        String(userProfile.id), SWIPE_COSTS.matching, 'discover_swipe_matching'
      );
      if (econ.success && econ.newBalance !== undefined) {
        updateProfile({ gold: econ.newBalance, goldBalance: econ.newBalance });
      }
      const result = await sendFriendRequest(
        uid, targetUser.name, targetUser.username,
        getUserAvatar(targetUser as any)
      );
      setConnectedUserIds(prev => new Set([...prev, uid]));
      if (result?.isMatch) onMatch?.(targetUser);
    } catch { toast.error('Failed to send request'); }
    finally { if (isMounted.current) setConnectingUser(null); }
  };

  // ── Connect button state helper ───────────────────────────────────────────────
  const getConnectState = (uid: string): 'idle' | 'loading' | 'accept' | 'sent' | 'friend' => {
    if (isFriend(uid))                                    return 'friend';
    if (hasPendingRequestTo(uid) || connectedUserIds.has(uid)) return 'sent';
    if (connectingUser === uid)                           return 'loading';
    if (hasPendingRequestFrom(uid))                       return 'accept';
    return 'idle';
  };

  // ── OPT-OUT GATE (mirrors V1) ─────────────────────────────────────────────────
  if (userProfile && userProfile.showInDiscover === false) {
    return (
      <div
        className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] flex flex-col items-center justify-center p-8 overflow-hidden"
        style={{
          backgroundImage: `url(${theme === 'dark' ? (darkPatternBg as string) : (lightPatternBg as string)})`,
          backgroundSize: '100% auto',
          backgroundRepeat: 'repeat-y',
        }}
      >
        <X className="w-10 h-10 text-[#4478ea] mb-6" />
        <h2 className="text-2xl font-bold text-white mb-3 text-center">You're Hidden from Discover</h2>
        <p className="text-white/50 text-sm leading-relaxed text-center mb-6">
          Re-enable <strong className="text-white">"Show me in Discover"</strong> in Edit Profile to browse and connect.
        </p>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'editProfile' }))}
          className="px-6 py-3 rounded-full text-white font-semibold text-sm"
          style={{ background: 'linear-gradient(90deg, #4478ea, #2d1ef1)' }}
        >
          Go to Edit Profile
        </button>
      </div>
    );
  }

  const balance = userProfile?.goldBalance ?? userProfile?.gold ?? 0;

  // Shared predicate: a user passes the active filter set when their age sits
  // inside the slider range, the location matches (if same-country is set),
  // and at least one selected interest is present in their tags.
  const passesActiveFilters = (yapper: User) => {
    const yapperAge = calculateAge(yapper.dateOfBirth) ?? yapper.age ?? null;
    if (yapperAge !== null) {
      if (yapperAge < activeFilters.ageRange[0] || yapperAge > activeFilters.ageRange[1]) return false;
    }
    if (activeFilters.location === 'same-country') {
      if ((yapper.country || '') !== (userProfile?.country || '')) return false;
    }
    if (activeFilters.interests.length > 0) {
      const yapperTags = new Set([...(yapper.identities ?? []), ...(yapper.interests ?? [])]);
      if (!activeFilters.interests.some(i => yapperTags.has(i))) return false;
    }
    return true;
  };

  // Apply activeFilters to Newest Yappers — same filter modal also drives
  // Similar to Me below via `filteredSimilarUsers`.
  // When interests are selected, sort by match count so users sharing the most tags appear first.
  const filteredNewYappers = (() => {
    // Hide anyone we're already connected to or have a pending outgoing request
    // for — they shouldn't appear as fresh people to discover.
    const filtered = newYappers.filter(u => {
      const uid = String(u.id);
      if (isFriend(uid) || hasPendingRequestTo(uid) || connectedUserIds.has(uid)) return false;
      return passesActiveFilters(u);
    });

    if (activeFilters.interests.length > 0) {
      filtered.sort((a, b) => {
        const tagsA = new Set([...(a.identities ?? []), ...(a.interests ?? [])]);
        const tagsB = new Set([...(b.identities ?? []), ...(b.interests ?? [])]);
        const matchA = activeFilters.interests.filter(i => tagsA.has(i)).length;
        const matchB = activeFilters.interests.filter(i => tagsB.has(i)).length;
        return matchB - matchA;
      });
    }

    return filtered;
  })();

  // Apply the same active filter set to Similar to Me. Affinity score order is preserved.
  const filteredSimilarUsers = similarUsers.filter(u => passesActiveFilters(u as unknown as User));

  // ── Render ───────────────────────────────────────────────────────────────────
  // Its OWN loader, not null.
  //
  // This used to return null on the assumption that the route-transition overlay
  // was covering it. It is not, in the two cases that matter most: arriving here
  // directly (a refresh, a deep link, or the app opening on Discover) never
  // starts a transition at all, because the overlay only fires on a pathname
  // CHANGE — and a load slower than the overlay's 5s ceiling outlives it. Both
  // left a black screen for the whole load, on the slowest screen in the app.
  //
  // Rendering the same loader here means the handoff is invisible when the
  // overlay IS up (identical visual, overlay simply sits on top) and correct
  // when it is not.
  if (isInitialLoading) return <ScreenLoader progress={loadProgress} />;

  return (
    <>
    <div
      className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] flex flex-col overflow-hidden"
      style={{
        backgroundImage: `url(${theme === 'dark' ? (darkPatternBg as string) : (lightPatternBg as string)})`,
        backgroundSize: '100% auto',
        backgroundRepeat: 'repeat-y',
        backgroundPosition: 'top center',
        visibility: swipeModeActive ? 'hidden' : 'visible',
      }}
    >

      {/* ═══════════════════════════════════════════════════════════════
          SINGLE SCROLLABLE AREA — header + search + sections all scroll together.
      ═══════════════════════════════════════════════════════════════ */}
      <div
        ref={scrollContainerRef}
        onScroll={onDiscoverScroll}
        className="flex-1 min-h-0 overflow-y-auto"
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'none',
          paddingBottom: 'calc(env(safe-area-inset-bottom) + 6.5rem)',
        }}
      >

        {/* Centered content column — max-w-2xl mirrors V1's layout */}
        <div
          className="w-full max-w-2xl mx-auto px-6 space-y-8"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}
        >

          {/* ── Header — exact YapTabScreen structure ── */}
          <div className="flex items-center justify-center relative min-h-[40px] mb-2">
            {/* Left: logo + bell */}
            <div className="absolute left-0 flex items-center gap-2">
              <img
                src={theme === 'dark' ? (logoImageWhite as any) : (logoImage as any)}
                alt="AfroYap"
                className="h-6 w-auto"
              />
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: SCREENS.NOTIFICATIONS }))}
                className="relative p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              >
                <Bell className="w-5 h-5 text-black dark:text-white" />
                {unreadCount > 0 && (
                  <span className={`absolute -top-2 -left-2 ${unreadCount > 9 ? 'min-w-[26px] h-[22px] px-1' : 'w-5 h-5'} bg-[#E74C3C] dark:bg-[#3498DB] text-white text-[11px] flex items-center justify-center rounded-full border-2 border-white dark:border-[#0D0D0D] font-bold`}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </motion.button>
            </div>

            {/* Center: title — h1 + NarnialOne matches YapTabScreen/ExploreScreen exactly */}
            <h1
              className="text-3xl text-black dark:text-white"
              style={{ fontFamily: "'NarnialOne', 'Narniaone', 'NARNIAONE', 'Poppins', sans-serif" }}
            >
              Discover
            </h1>

            {/* Right: gold counter — matches YapTab style */}
            <div className="absolute right-0">
              <div className="flex items-center gap-1.5 bg-black/5 dark:bg-white/5 px-3 py-1.5 rounded-full border border-black/10 dark:border-white/10 backdrop-blur-sm opacity-50 cursor-not-allowed select-none">
                <img src={goldIcon as any} alt="Gold" className="h-4 w-auto object-contain" />
                <span className="text-black dark:text-white text-xs font-bold">{Number(balance).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* ── Search bar ── */}
          <div className="flex gap-2">
            <div className="flex-1 relative" ref={searchWrapperRef}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
              <Input
                value={searchQuery}
                onChange={handleSearchChange}
                onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
                placeholder="Search @username…"
                className="w-full pl-10 h-10 text-white placeholder:text-white/30 rounded-xl text-sm"
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.10)' }}
              />
            </div>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setFilterModalOpen(true)}
              className="relative w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-brand-gradient text-white"
              aria-label="Filter"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {(activeFilters.location === 'same-country' || activeFilters.interests.length > 0) && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-yellow-400 border border-[#0D0D0D]" />
              )}
            </motion.button>
          </div>


          {/* ── Three sections ── */}
          <div className="space-y-10">

          {/* ─────────────────────────────────────────────────────────────
              SECTION 1: TRENDING YAPS
          ───────────────────────────────────────────────────────────── */}
          <section data-tour="guide-trending">
            <SectionHeader title="Trending Yaps" />
            <div className="mt-6">
            <HScrollDeck>
              {loadingTrending ? (
                [1, 2, 3].map(i => (
                  <SkeletonCard key={i} className="shrink-0 h-[200px]" style={{ width: 'calc(50% - 6px)', minWidth: 140 } as any} />
                ))
              ) : visibleTrendingYaps.length === 0 ? (
                <div className="w-full min-h-[100px] py-8 flex flex-col items-center justify-center gap-2">
                  <TrendingIcon className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.40)' }} />
                  <p className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.60)' }}>No Trending Yaps yet</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.38)' }}>Legions set what's hot</p>
                </div>
              ) : visibleTrendingYaps.map(yap => (
                <TrendingYapCard
                  key={yap.id}
                  yap={yap}
                  isFriendAuthor={(() => {
                    const aid = (yap as any).userId || (yap as any).authorId;
                    return !!aid && String(aid) !== String(userProfile?.id) && isFriend(String(aid));
                  })()}
                  onTap={() => navigate(`/yap/${yap.id}`, { state: { yap } })}
                />
              ))}
            </HScrollDeck>
            </div>
          </section>

          {/* ─────────────────────────────────────────────────────────────
              SECTION 2: SIMILAR INTERESTS TO ME
          ───────────────────────────────────────────────────────────── */}
          <section data-tour="guide-similar">
            <SectionHeader title="Similar to Me" />
            {/* Same flag-overflow trick as New Yappers: 10px + 14px(paddingTop) = 24px gap. */}
            <div style={{ marginTop: '10px' }}>
            {/* Arrows centre on the photos rather than the whole card, for the
                same reason as Newest Yappers: the badge now sits in the photo's
                bottom-left corner, and the photo is this card's left column, so a
                deck-centred arrow lands on top of it. */}
            <HScrollDeck paddingTop={14} paddingBottom={4} centerArrowsOnMedia>
              {loadingSimilar ? (
                [1, 2].map(i => (
                  <div key={i} className="shrink-0 rounded-2xl flex flex-col bg-white/[0.04]"
                       style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="flex items-start">
                      <div className="shrink-0 bg-white/8 animate-pulse rounded-none rounded-tl-2xl rounded-bl-2xl"
                           style={{ width: 'calc((min(100vw, 672px) - 64px) / 3)', aspectRatio: '3/4' }} />
                      <div className="p-4 flex flex-col gap-2" style={{ minWidth: '220px' }}>
                        <div className="h-5 bg-white/8 animate-pulse rounded w-3/4" />
                        <div className="h-3 bg-white/8 animate-pulse rounded w-1/2" />
                        <div className="h-px bg-white/8 animate-pulse w-full mt-1" />
                        {[1,2,3].map(r => (
                          <div key={r} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' }}>
                            {[1,2,3].map(c => <div key={c} className="h-6 bg-white/8 animate-pulse rounded-full" />)}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              ) : filteredSimilarUsers.length === 0 ? (
                <div className="w-full min-h-[100px] py-8 flex flex-col items-center justify-center gap-2">
                  <Sparkles className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.40)' }} />
                  {similarUsers.length > 0 ? (
                    <>
                      <p className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.60)' }}>No matches with your filters</p>
                      <button
                        className="text-xs underline"
                        style={{ color: '#4478ea' }}
                        onClick={() => setActiveFilters({ location: 'international', ageRange: viewerDefaultAgeRange, interests: [], tribes: [] })}
                      >
                        Clear filters
                      </button>
                    </>
                  ) : (userProfile?.identities?.length ?? 0) < 4 ? (
                    <>
                      <p className="text-sm font-semibold text-center" style={{ color: 'rgba(255,255,255,0.60)' }}>
                        Add more interests to find matches
                      </p>
                      <button
                        className="text-xs underline"
                        style={{ color: '#4478ea' }}
                        onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'editProfile' }))}
                      >
                        Edit Profile →
                      </button>
                    </>
                  ) : (
                    <p className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.60)' }}>
                      No close matches found this session
                    </p>
                  )}
                </div>
              ) : filteredSimilarUsers.map(user => {
                const uid = String(user.id);
                const portfolio = [...(user.portfolioPreviews ?? []), ...(user.portfolio ?? [])].filter(Boolean);
                const imgs = portfolio.length > 0 ? portfolio : [user.image, user.avatar].filter(Boolean) as string[];
                const connectState = getConnectState(uid);
                const tags = buildTagItems(user);

                const connectBg =
                  connectState === 'friend'  ? 'rgba(30,107,44,0.85)' :
                  connectState === 'sent'    ? 'rgba(255,255,255,0.08)' :
                  connectState === 'accept'  ? 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)' :
                  /* idle */                   'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)';

                const connectLabel =
                  connectState === 'idle'    ? `Connect · ${SWIPE_COSTS.matching} Gold` :
                  connectState === 'loading' ? 'Sending…' :
                  connectState === 'accept'  ? 'Accept' :
                  connectState === 'sent'    ? 'Request Sent' :
                  /* friend */                 'Connected ✓';

                const userResidence = (user as any).home_residence || (user as any).location || '';

                // Rank badge shown alongside the connect button in Tagboard's header-right slot
                const rankBadgeEl = user.rank && ['watcher', 'legion', 'verified', 'union'].includes(user.rank)
                  ? <RankBadge rank={user.rank} size={14} />
                  : null;
                const verifiedBadgeEl = (user as any).verifiedBadge ? <VerifiedBadge size={13} /> : null;

                // Compact connect button passed into Tagboard's header-right slot
                const connectBtn = connectState !== 'friend' ? (
                  <motion.button
                    whileTap={connectState === 'idle' || connectState === 'accept' ? { scale: 0.95 } : {}}
                    disabled={connectState === 'loading' || connectState === 'sent'}
                    onClick={(e) => { e.stopPropagation(); connectWithUser(user); }}
                    className="flex items-center gap-1 text-white font-bold rounded-full shrink-0"
                    style={{
                      fontSize: '9px',
                      height: 26,
                      padding: '0 8px',
                      background: connectBg,
                      cursor: (connectState === 'sent') ? 'default' : 'pointer',
                    }}
                  >
                    {connectState === 'loading' && <Loader2 className="w-3 h-3 animate-spin" />}
                    {connectState === 'idle' && (
                      <>
                        <img src={goldIcon as any} alt="" className="h-2.5 w-auto object-contain" />
                        <span>30 Gold</span>
                      </>
                    )}
                    {connectState === 'accept' && <><UserPlus className="w-3 h-3" /><span>Accept</span></>}
                    {connectState === 'sent' && <span>Sent</span>}
                  </motion.button>
                ) : (
                  <FriendBadge className="shrink-0" />
                );

                return (
                  <div
                    key={uid}
                    className="shrink-0 rounded-2xl flex flex-col"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.07)',
                    }}
                  >
                    {/* Top row: photo left, Tagboard right */}
                    <div className="flex items-start">
                      {/* Left: photo card */}
                      <PressableCard
                        className="relative shrink-0 self-start cursor-pointer"
                        style={{
                          width: 'calc((min(100vw, 672px) - 64px) / 3)',
                          aspectRatio: '3/4',
                          zIndex: 1,
                        }}
                        onClick={() => setPreviewUser(user)}
                      >
                        <MiniDiscoverCard
                          user={user}
                          images={imgs}
                          intervalMs={3000}
                          showActivityBadge
                          activityBadgePosition="bottom-left"
                          className="rounded-none rounded-tl-2xl rounded-bl-2xl"
                        />
                      </PressableCard>

                      {/* Right column: Tagboard + bio below it */}
                      <div
                        className="cursor-pointer flex flex-col"
                        onClick={() => setPreviewUser(user)}
                      >
                        <Tagboard
                          displayName={(() => {
                            const age = calculateAge((user as any).dateOfBirth) ?? (user as any).age ?? null;
                            return age ? `${user.name}, ${age}` : user.name;
                          })()}
                          username={user.username}
                          residence={userResidence}
                          headerRight={
                            <div className="flex items-center gap-1">
                              {verifiedBadgeEl}
                              {rankBadgeEl}
                              {connectBtn}
                            </div>
                          }
                          tags={tags}
                          maxTags={9}
                          className="rounded-none rounded-tr-2xl"
                        />
                        <ActivityStatus userId={user.id} style={{ fontSize: 10, padding: '6px 12px 0' }} />
                        {user.bio ? (
                          <p
                            className="px-3 pt-2 pb-3 leading-snug whitespace-pre-wrap"
                            style={{
                              fontSize: '9.5px',
                              color: 'rgba(255,255,255,0.60)',
                              fontStyle: 'italic',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            "{user.bio}"
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </HScrollDeck>
            </div>
          </section>

          {/* ─────────────────────────────────────────────────────────────
              SECTION 3: GROUP UPDATES
          ───────────────────────────────────────────────────────────── */}
          <section data-tour="guide-groups">
            <SectionHeader title="Groups" />
            <div style={{ marginTop: '10px' }}>
            <HScrollDeck>
              {(loadingGroups && taggedGroups === null) || loadingTaggedGroups ? (
                [1, 2].map(i => (
                  <div key={i} className="shrink-0" style={{ width: 'calc(80% - 8px)', minWidth: 280 }}>
                    <SkeletonCard className="h-[160px] w-full" />
                  </div>
                ))
              ) : displayedGroups.length === 0 ? (
                <div className="w-full min-h-[100px] py-8 flex flex-col items-center justify-center gap-2">
                  <Users className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.40)' }} />
                  <p className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.60)' }}>
                    {taggedGroups !== null ? 'No groups match your selected tags' : 'No active groups yet'}
                  </p>
                  {taggedGroups !== null && (
                    <button
                      className="text-xs underline"
                      style={{ color: '#4478ea' }}
                      onClick={() => setActiveFilters(prev => ({ ...prev, interests: [] }))}
                    >
                      Clear tag filter
                    </button>
                  )}
                </div>
              ) : displayedGroups.map(group => (
                <GroupUpdateCard
                  key={`${group.type}-${group.id}`}
                  group={group}
                  onTap={() => navigate(group.type === 'tribe' ? `/tribe/${group.id}` : `/forum/${group.id}`)}
                />
              ))}
            </HScrollDeck>
            </div>
          </section>

          {/* ─────────────────────────────────────────────────────────────
              SECTION 4: NEWEST YAPPERS
          ───────────────────────────────────────────────────────────── */}
          <section data-tour="guide-newest">
            {/* Header row with refresh button */}
            <div className="w-full pt-4">
              <div className="flex items-center justify-between mb-3">
                <div
                  className="font-american-captain text-white uppercase leading-none"
                  style={{ fontSize: '1.5rem', letterSpacing: '0.04em', fontFamily: "'AmericanCaptain', 'Poppins', sans-serif" }}
                >
                  Newest Yappers
                </div>
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  disabled={isRefreshingYappers || loadingYappers}
                  onClick={() => setYappersRefreshKey(k => k + 1)}
                  className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.65)' }}
                >
                  {isRefreshingYappers
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <span>↻</span>}
                  Refresh
                </motion.button>
              </div>
              <div className="w-full h-px" style={{ background: 'rgba(255,255,255,0.18)' }} />
            </div>
            <div style={{ marginTop: '10px' }}>
            {/* Arrows centre on the photos, not the whole card: the badge moved
                to the bottom-left of the photo and the deck-centred arrow sat
                exactly there. */}
            <HScrollDeck paddingTop={14} paddingBottom={6} centerArrowsOnMedia>
              {loadingYappers ? (
                [1, 2, 3].map(i => (
                  <div key={i} className="shrink-0 flex flex-col gap-1 self-start" style={{ width: 'calc((100% - 24px) / 3)' }}>
                    <div className="rounded-2xl bg-white/8 animate-pulse w-full" style={{ aspectRatio: '3/4' }} />
                    <div className="px-0.5 flex flex-col gap-1">
                      <div className="rounded bg-white/8 animate-pulse h-3 w-3/4" />
                      <div className="rounded bg-white/8 animate-pulse h-2.5 w-1/2" />
                    </div>
                  </div>
                ))
              ) : filteredNewYappers.length === 0 ? (
                <div className="w-full min-h-[100px] py-8 flex flex-col items-center justify-center gap-2">
                  <Users className="w-8 h-8" style={{ color: 'rgba(255,255,255,0.40)' }} />
                  {newYappers.length > 0 ? (
                    <>
                      <p className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.60)' }}>No Yappers match your filters</p>
                      <button
                        className="text-xs underline"
                        style={{ color: '#4478ea' }}
                        onClick={() => setActiveFilters({ location: 'international', ageRange: viewerDefaultAgeRange, interests: [], tribes: [] })}
                      >
                        Clear filters
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.60)' }}>No new Yappers this month</p>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.38)' }}>Check back soon</p>
                    </>
                  )}
                </div>
              ) : filteredNewYappers.slice(0, 10).map(yapper => {
                const portfolio = [...(yapper.portfolioPreviews ?? []), ...(yapper.portfolio ?? [])].filter(Boolean);
                const imgs = portfolio.length > 0 ? portfolio : [yapper.image, yapper.avatar].filter(Boolean) as string[];
                const yapperConnectState = getConnectState(String(yapper.id));
                const residence = (yapper as any).home_residence || (yapper as any).location || '';
                const yapperDisplayAge = calculateAge(yapper.dateOfBirth) ?? yapper.age ?? null;
                const bio = yapper.bio || '';
                const yapperSimilar = isSimilarToMe(userProfile, yapper);
                const yapperCost = yapperSimilar ? SWIPE_COSTS.matching : SWIPE_COSTS.classic;
                return (
                  <div
                    key={String(yapper.id)}
                    className="shrink-0 flex flex-col self-start"
                    style={{ width: 'calc((100% - 24px) / 3)' }}
                  >
                    <motion.div
                      whileTap={{ scale: 0.96 }}
                      onClick={() => setPreviewUser(yapper)}
                      className="relative w-full cursor-pointer"
                      style={{ aspectRatio: '3/4' }}
                    >
                      <MiniDiscoverCard
                        user={yapper}
                        images={imgs}
                        intervalMs={3000}
                        showActivityBadge
                        activityBadgePosition="bottom-left"
                      />
                    </motion.div>

                    {/* Name and age/location get the FULL card width, and the
                        connect button sits on its own row underneath.
                        Sharing a row with a ~52px button left roughly 50px for
                        the name at three cards across a phone, which is why
                        every name truncated to "Ja..." and the age/location line
                        collapsed to "30 · ...". */}
                    <div className="px-0.5 mt-3 flex flex-col">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1 leading-tight">
                          <p className="truncate" style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff' }}>
                            {yapper.name}
                          </p>
                          {yapper.verifiedBadge && <VerifiedBadge size={13} />}
                          {yapper.rank && ['watcher', 'legion', 'verified', 'union'].includes(yapper.rank) && (
                            <RankBadge rank={yapper.rank} size={13} />
                          )}
                        </div>
                        {(yapperDisplayAge || residence) && (
                          <p className="leading-tight truncate mt-0.5" style={{ fontSize: '10px', color: 'rgba(255,255,255,0.75)' }}>
                            {[yapperDisplayAge, residence].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        {yapperSimilar && yapperConnectState !== 'friend' && (
                          <div className="mt-1">
                            <SimilarBadge />
                          </div>
                        )}
                      </div>
                      {/* 10px, not 6: at 6 the button sat against the
                          age/location line above it. */}
                      <div className="flex items-center" style={{ marginTop: 10 }}>
                      {yapperConnectState !== 'friend' && (
                        <motion.button
                          whileTap={yapperConnectState === 'idle' || yapperConnectState === 'accept' ? { scale: 0.95 } : {}}
                          disabled={yapperConnectState === 'loading' || yapperConnectState === 'sent'}
                          onClick={(e) => { e.stopPropagation(); connectNewYapper(yapper); }}
                          className="shrink-0 h-7 px-2 flex items-center gap-1 text-white font-bold rounded-full"
                          style={{
                            fontSize: '9px',
                            background: yapperConnectState === 'sent'
                              ? 'rgba(255,255,255,0.08)'
                              : 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)',
                            cursor: yapperConnectState === 'sent' ? 'default' : 'pointer',
                          }}
                        >
                          {yapperConnectState === 'loading' && <Loader2 className="w-3 h-3 animate-spin" />}
                          {yapperConnectState === 'idle' && (
                            <>
                              <img src={goldIcon as any} alt="" className="h-2.5 w-auto object-contain" />
                              <span>{yapperCost} Gold</span>
                            </>
                          )}
                          {yapperConnectState === 'accept' && <span>Accept</span>}
                          {yapperConnectState === 'sent' && <span>Sent</span>}
                        </motion.button>
                      )}
                      {yapperConnectState === 'friend' && <FriendBadge className="shrink-0" />}
                      </div>
                    </div>

                    {bio ? (
                      <p
                        className={`px-0.5 ${yapperSimilar && yapperConnectState !== 'friend' ? 'mt-0.5' : 'mt-2'} pb-1.5 leading-snug whitespace-pre-wrap`}
                        style={{
                          fontSize: '9.5px',
                          color: 'rgba(255,255,255,0.60)',
                          fontStyle: 'italic',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        "{bio}"
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </HScrollDeck>
            </div>
          </section>

        </div>{/* end space-y-8 (sections) */}
        </div>{/* end max-w-2xl content column */}
      </div>{/* end scrollable flex-1 */}

      {/* ═══════════════════════════════════════════════════════════════
          FLOATING SWIPE BUTTON — hidden when V1 overlay is active so
          it never conflicts with the overlay's z-index.
      ═══════════════════════════════════════════════════════════════ */}
      {!swipeModeActive && <div
        style={{
          position: 'fixed',
          right: '20px',
          left: 'auto',
          bottom: 'calc(env(safe-area-inset-bottom) + 88px)',
          zIndex: 40,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <motion.button
          whileTap={{ scale: 0.88 }}
          onClick={() => setSwipeModeActive(true)}
          aria-label="Classic swipe"
          data-tour="guide-discover"
          className="w-14 h-14 rounded-full flex items-center justify-center text-white"
          style={{
            background: 'linear-gradient(135deg, #4478ea 0%, #2d1ef1 100%)',
            boxShadow: '0 4px 20px rgba(68,120,234,0.55), 0 2px 8px rgba(0,0,0,0.4)',
          }}
        >
          <Zap className="w-6 h-6" />
        </motion.button>
        <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.90)', fontWeight: 700, letterSpacing: '0.06em' }}>
          Swipe
        </span>
      </div>}

    </div>{/* end V2 root — visibility:hidden when overlay active */}

    {/* ═══════════════════════════════════════════════════════════════
        V1 SWIPE OVERLAY — sibling of the V2 root, NOT a descendant.
        Renders outside the V2 compositing tree so no GPU layer conflict
        is possible. z-index:9999 beats everything in the app.
    ═══════════════════════════════════════════════════════════════ */}
    {swipeModeActive && (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          background: '#0D0D0D',
        }}
      >
        <DiscoverScreen
          onMatch={onMatch ?? (() => {})}
          onReturnToV2={() => setSwipeModeActive(false)}
        />
      </div>
    )}

    {/* ═══════════════════════════════════════════════════════════════
        DISCOVER FILTER MODAL — applies to Newest Yappers only
    ═══════════════════════════════════════════════════════════════ */}
    {userProfile && (
      <DiscoverFilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApplyFilters={(f) => setActiveFilters(f)}
        currentFilters={activeFilters}
      />
    )}

    {/* ═══════════════════════════════════════════════════════════════
        INSUFFICIENT FUNDS MODAL (Connect · 30 Gold)
    ═══════════════════════════════════════════════════════════════ */}
    {/* Profile preview modal — opened by tapping any user card in V2 */}
    {previewUser && (
      <UserDetailModal
        isOpen={!!previewUser}
        onClose={() => setPreviewUser(null)}
        user={previewUser}
      />
    )}

    {userProfile && (
      <InsufficientFundsModal
        isOpen={showInsufficientFunds}
        onClose={() => setShowInsufficientFunds(false)}
        onGoToMarket={() => {
          setShowInsufficientFunds(false);
          window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'store' }));
        }}
        requiredAmount={SWIPE_COSTS.matching}
        currentAmount={balance}
        customMessage={`You need ${SWIPE_COSTS.matching} gold to connect with someone like this. You have ${balance} gold.`}
      />
    )}

    {/* Search dropdown — fixed position sibling, outside every overflow container */}
    {showSearchDropdown && searchWrapperRef.current && (() => {
      const r = searchWrapperRef.current!.getBoundingClientRect();
      return (
        <div
          style={{
            position: 'fixed',
            top: r.bottom + 4,
            left: r.left,
            width: r.width,
            zIndex: 9999,
            background: '#1a1a1a',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }}
        >
          {visibleSearchResults.length > 0 ? visibleSearchResults.map(u => (
            <div
              key={u.id}
              className="px-4 py-3 flex items-center gap-3 cursor-pointer"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
              onClick={() => {
                window.dispatchEvent(new CustomEvent('navigateToFriendProfile', { detail: u }));
                setSearchQuery('');
                setShowSearchDropdown(false);
              }}
            >
              <img src={u.image || u.avatar} alt={u.name} className="w-9 h-9 rounded-full object-cover shrink-0" />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-white text-sm font-semibold truncate">{u.name}</p>
                  {isFriend(String(u.id)) && <FriendBadge className="shrink-0" />}
                  {(u as any).accountHidden && (
                    <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-white/10 text-white/50">Hidden</span>
                  )}
                </div>
                <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  @{(u.username || '').replace(/^@/, '')}
                </p>
                <ActivityStatus userId={u.id} style={{ fontSize: 11, marginTop: 1 }} />
              </div>
            </div>
          )) : (
            <div className="px-4 py-5 text-center text-sm" style={{ color: 'rgba(255,255,255,0.30)' }}>
              No results found
            </div>
          )}
        </div>
      );
    })()}
    </>
  );
}
